import { formatDurationMinutes } from '../../../utils/displayFormat';

// Pure helpers for inspecting a driver's recorded GPS trail: which phone each
// reading came from, where the phone stopped reporting, which recorded point
// sits under the cursor, how far the trail actually runs, and how many points
// the map can afford to draw. No Leaflet, no React — the layer builder, the
// Stops panel and their tests use these directly.

const MS_PER_MINUTE = 60 * 1000;
const EARTH_RADIUS_MILES = 3958.8;
// Douglas–Peucker tolerance for the DRAWN line, in degrees (0.0001° ≈ 11 m):
// readings closer than this to the line between their neighbors are GPS
// jitter, not a turn. Only the line is thinned — dots and the hover card
// still use every recorded reading.
export const SIMPLIFY_EPSILON = 0.00004; // ≈ 4.5 m

// Phones report every couple of minutes while the app is foregrounded. A
// silence longer than this means the trail is missing real driving — the map
// says so instead of drawing a straight line through the unknown.
export const GPS_GAP_MIN = 5;

const toRad = (degrees) => (degrees * Math.PI) / 180;

/**
 * One chain of recorded positions per driver, in the order they were
 * recorded. Routes are sometimes worked by two drivers at once, and a single
 * line (or distance sum) through both phones would jump back and forth
 * between them. Positions with no driver attribution ride together.
 *
 * @param {Array<{driverId?: string}>} points Oldest first.
 * @returns {Array<[string, Array<object>]>} `[driverKey, points]` entries.
 */
export function groupTrailByDriver(points) {
  const chains = new Map();
  points.forEach((point) => {
    const key = point.driverId || 'unattributed';
    if (!chains.has(key)) {
      chains.set(key, []);
    }
    chains.get(key).push(point);
  });
  return Array.from(chains.entries());
}

/**
 * Break a trail wherever reporting stopped for longer than `GPS_GAP_MIN`.
 *
 * @param {Array<{timestampMs: number}>} points Trail points, oldest first.
 * @returns {{runs: Array<Array<object>>, gaps: Array<{from: object, to: object, minutes: number}>}}
 *          `runs` are the continuously reported stretches (never empty; a run
 *          of one point is allowed and simply draws no line); `gaps` are the
 *          silences between consecutive runs, in the same order.
 */
export function splitTrailAtGaps(points) {
  if (!Array.isArray(points) || points.length === 0) {
    return { runs: [], gaps: [] };
  }
  const runs = [[points[0]]];
  const gaps = [];
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    const minutes =
      (current.timestampMs - previous.timestampMs) / MS_PER_MINUTE;
    if (minutes > GPS_GAP_MIN) {
      gaps.push({ from: previous, to: current, minutes });
      runs.push([current]);
    } else {
      runs[runs.length - 1].push(current);
    }
  }
  return { runs, gaps };
}

/**
 * The recorded point closest to a map position, for snapping a hover card to
 * real telemetry instead of the arbitrary spot under the cursor. Planar
 * distance with longitude scaled by the latitude cosine (as in
 * `mapGeometry.pathMidpoint`) — exact enough at city scale and far cheaper
 * than haversine on every mousemove.
 *
 * @param {Array<{latitude: number, longitude: number}>} points
 * @param {{lat: number, lng: number}} position Cursor position (a Leaflet LatLng works).
 * @returns {object|null} The nearest point, or null with nothing to compare.
 */
export function nearestTrailPoint(points, position) {
  if (!Array.isArray(points) || points.length === 0 || !position) {
    return null;
  }
  let nearest = null;
  let nearestDistance = Infinity;
  points.forEach((point) => {
    const meanLat = (point.latitude + position.lat) / 2;
    const distance = Math.hypot(
      point.latitude - position.lat,
      (point.longitude - position.lng) * Math.cos(toRad(meanLat)),
    );
    if (distance < nearestDistance) {
      nearest = point;
      nearestDistance = distance;
    }
  });
  return nearest;
}

// Perpendicular distance from `point` to the segment start→end, in the same
// planar degrees the tolerance is expressed in (a degenerate segment falls
// back to the distance to its start).
function perpendicularDistance(point, segmentStart, segmentEnd) {
  const [pointLat, pointLng] = point;
  const [startLat, startLng] = segmentStart;
  const [endLat, endLng] = segmentEnd;
  const segmentLat = endLat - startLat;
  const segmentLng = endLng - startLng;
  const segmentLength = Math.hypot(segmentLat, segmentLng);
  if (segmentLength === 0) {
    return Math.hypot(pointLat - startLat, pointLng - startLng);
  }
  const cross =
    segmentLat * (startLng - pointLng) - segmentLng * (startLat - pointLat);
  return Math.abs(cross) / segmentLength;
}

/**
 * Douglas–Peucker line simplification: keeps the vertices that shape the
 * path and drops the ones within `epsilon` of the line between their kept
 * neighbors. Iterative (an explicit stack), so a full day's run can never
 * overflow the call stack. The first and last vertex always survive.
 *
 * @param {Array<[number, number]>} latLngs Vertices as [lat, lng].
 * @param {number} [epsilon] Tolerance in degrees.
 * @returns {Array<[number, number]>} A subset of the input, in order.
 */
export function simplifyLatLngs(latLngs, epsilon = SIMPLIFY_EPSILON) {
  if (latLngs.length < 3) {
    return latLngs;
  }
  const keep = new Array(latLngs.length).fill(false);
  keep[0] = true;
  keep[latLngs.length - 1] = true;
  const stack = [[0, latLngs.length - 1]];
  while (stack.length > 0) {
    const [start, end] = stack.pop();
    let farthestDistance = 0;
    let farthestIndex = -1;
    for (let index = start + 1; index < end; index += 1) {
      const distance = perpendicularDistance(
        latLngs[index],
        latLngs[start],
        latLngs[end],
      );
      if (distance > farthestDistance) {
        farthestDistance = distance;
        farthestIndex = index;
      }
    }
    if (farthestDistance > epsilon && farthestIndex !== -1) {
      keep[farthestIndex] = true;
      stack.push([start, farthestIndex], [farthestIndex, end]);
    }
  }
  return latLngs.filter((latLng, index) => keep[index]);
}

// Great-circle distance between two recorded positions, in miles.
function haversineMiles(pointA, pointB) {
  const latA = toRad(pointA.latitude);
  const latB = toRad(pointB.latitude);
  const deltaLat = latB - latA;
  const deltaLng = toRad(pointB.longitude - pointA.longitude);
  const halfChord =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(latA) * Math.cos(latB) * Math.sin(deltaLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(halfChord));
}

/**
 * Miles actually driven along a recorded trail: the summed distance between
 * consecutive readings, skipping the stretches the phone never reported (a
 * straight line through a GPS gap would invent mileage). Summed per driver,
 * since a shared route's trail interleaves two phones in time order.
 *
 * @param {Array<{latitude: number, longitude: number, timestampMs: number,
 *   driverId?: string}>} points Oldest first.
 * @returns {number|null} Miles, or null with fewer than two points.
 */
export function trailDistanceMiles(points) {
  if (!Array.isArray(points) || points.length < 2) {
    return null;
  }
  let miles = 0;
  groupTrailByDriver(points).forEach(([, driverPoints]) => {
    splitTrailAtGaps(driverPoints).runs.forEach((run) => {
      for (let index = 1; index < run.length; index += 1) {
        miles += haversineMiles(run[index - 1], run[index]);
      }
    });
  });
  return miles;
}

/**
 * How many points to skip between drawn dots so at most `budget` of them
 * reach Leaflet — a full day of pings is thousands of markers otherwise.
 *
 * @param {number} count Points available.
 * @param {number} budget Most points to draw.
 * @returns {number} An integer stride of 1 or more.
 */
export function strideForBudget(count, budget) {
  if (!Number.isFinite(count) || !Number.isFinite(budget) || budget < 1) {
    return 1;
  }
  return Math.max(1, Math.ceil(count / budget));
}

// Gap length as a label ("11m", "1h 30m") — the same humanized form every
// other duration on the Monitoring tab uses.
export function formatGapDuration(minutes) {
  return formatDurationMinutes(minutes);
}
